import { create } from 'zustand';
import { http } from '@/services/api';

export type RoadblockerStatus = 'active' | 'scheduled' | 'paused' | 'draft';
export type RoadblockerAudience = 'all' | 'new' | 'returning' | 'guests' | 'members';
export type RoadblockerFrequency = 'once_per_session' | 'once_per_day' | 'once_ever' | 'every_visit';
export type RoadblockerPlacement = 'modal_center' | 'modal_corner' | 'top_banner' | 'bottom_banner';

export interface Roadblocker {
  id: string;
  name: string;
  title: string;
  body: string;
  imageUrl?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  dismissLabel?: string;
  status: RoadblockerStatus;
  placement: RoadblockerPlacement;
  audience: RoadblockerAudience;
  frequency: RoadblockerFrequency;
  startAt?: string;
  endAt?: string;
  delaySeconds: number;
  accentColor: string;
  createdAt: string;
  updatedAt: string;
  impressions: number;
  clicks: number;
}

export type RoadblockerInput = Omit<Roadblocker, 'id' | 'createdAt' | 'updatedAt' | 'impressions' | 'clicks'>;

// Route through the shared `http` axios instance so requests carry the admin
// auth token and benefit from the 401 → token-refresh interceptor. The
// roadblocker management endpoints now require auth (roadblockers.manage), so a
// plain unauthenticated fetch would 401.
type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';
async function apiRequest<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const res = await http.request({ method, url: `/roadblockers${path}`, data: body });
  const payload = res.data;
  return (payload && typeof payload === 'object' && 'data' in payload
    ? payload.data
    : payload) as T;
}

interface RoadblockerState {
  roadblockers: Roadblocker[];
  loaded: boolean;
  loading: boolean;
  load: () => Promise<void>;
  add: (input: RoadblockerInput) => Promise<Roadblocker>;
  update: (id: string, patch: Partial<RoadblockerInput>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  duplicate: (id: string) => Promise<void>;
  setStatus: (id: string, status: RoadblockerStatus) => Promise<void>;
}

export const useRoadblockerStore = create<RoadblockerState>()((set, get) => ({
  roadblockers: [],
  loaded: false,
  loading: false,

  load: async () => {
    const { loading, loaded } = get();
    if (loading || loaded) return;
    set({ loading: true });
    try {
      const data = await apiRequest<Roadblocker[]>('GET', '');
      set({ roadblockers: data, loaded: true });
    } finally {
      set({ loading: false });
    }
  },

  add: async (input) => {
    const created = await apiRequest<Roadblocker>('POST', '', input);
    set({ roadblockers: [created, ...get().roadblockers] });
    return created;
  },

  update: async (id, patch) => {
    const updated = await apiRequest<Roadblocker>('PATCH', `/${id}`, patch);
    set({ roadblockers: get().roadblockers.map((r) => (r.id === id ? updated : r)) });
  },

  remove: async (id) => {
    await apiRequest<void>('DELETE', `/${id}`);
    set({ roadblockers: get().roadblockers.filter((r) => r.id !== id) });
  },

  duplicate: async (id) => {
    const src = get().roadblockers.find((r) => r.id === id);
    if (!src) return;
    const input: RoadblockerInput = {
      name: `${src.name} (copy)`,
      title: src.title,
      body: src.body,
      imageUrl: src.imageUrl,
      ctaLabel: src.ctaLabel,
      ctaUrl: src.ctaUrl,
      dismissLabel: src.dismissLabel,
      status: 'draft',
      placement: src.placement,
      audience: src.audience,
      frequency: src.frequency,
      startAt: src.startAt,
      endAt: src.endAt,
      delaySeconds: src.delaySeconds,
      accentColor: src.accentColor,
    };
    const created = await apiRequest<Roadblocker>('POST', '', input);
    set({ roadblockers: [created, ...get().roadblockers] });
  },

  setStatus: async (id, status) => {
    const updated = await apiRequest<Roadblocker>('PATCH', `/${id}`, { status });
    set({ roadblockers: get().roadblockers.map((r) => (r.id === id ? updated : r)) });
  },
}));

export function isLive(rb: Roadblocker, now = Date.now()): boolean {
  if (rb.status !== 'active') return false;
  if (rb.startAt && new Date(rb.startAt).getTime() > now) return false;
  if (rb.endAt && new Date(rb.endAt).getTime() < now) return false;
  return true;
}
